import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import PublicShell from "@/components/public/PublicShell";
import VaultInsight from "@/components/insights/VaultInsight";
import QuickCheck from "@/components/insights/QuickCheck";
import { getArticle } from "@/data/vault_articles";
import { VAULT_INSIGHTS } from "@/data/vault_insights";
import { ArrowRight, ArrowLeft, Clock } from "lucide-react";

/**
 * VAULT Journal · Article Reader.
 *
 * Editorial article page. Renders one hand-drafted VAULT_ARTICLES
 * entry with a signature MIDNIGHT LUXE hero, an inline VAULT INSIGHT
 * mid-article, a Quick Check near the end, related-reading tiles, and
 * a progressive-CTA finale that never interrupts reading.
 */
const TONE_STOP = {
    gold:    "linear-gradient(140deg,#E6D5A8 0%,#C6A86B 60%,#8f6f38 100%)",
    emerald: "linear-gradient(140deg,#A7F3D0 0%,#10B981 55%,#065F46 100%)",
    coral:   "linear-gradient(140deg,#FED7AA 0%,#F97316 55%,#7C2D12 100%)",
    teal:    "linear-gradient(140deg,#99F6E4 0%,#14B8A6 55%,#134E4A 100%)",
    violet:  "linear-gradient(140deg,#DDD6FE 0%,#8B5CF6 55%,#4C1D95 100%)",
};

function ReadingProgress() {
    const [pct, setPct] = useState(0);
    useEffect(() => {
        const onScroll = () => {
            const h = document.documentElement;
            const scrolled = h.scrollTop;
            const height = h.scrollHeight - h.clientHeight;
            setPct(Math.min(100, Math.max(0, (scrolled / Math.max(1, height)) * 100)));
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    return <div className="ar-progress" style={{ width: `${pct}%` }} aria-hidden />;
}

function RenderSection({ s, i }) {
    switch (s.kind) {
        case "lede":     return <p className="ar-lede" data-testid={`sec-lede-${i}`}>{s.text}</p>;
        case "para":     return <p className="ar-para" data-testid={`sec-para-${i}`}>{s.text}</p>;
        case "h2":       return <h2 className="ar-h2"  data-testid={`sec-h2-${i}`}>{s.text}</h2>;
        case "pull":     return <blockquote className="ar-pull" data-testid={`sec-pull-${i}`}>&ldquo;{s.text}&rdquo;</blockquote>;
        case "checklist":
            return (
                <div className="ar-checklist" data-testid={`sec-checklist-${i}`}>
                    <ul>
                        {s.text.split(" · ").map((line, ix) => (
                            <li key={ix}><strong>{line.split(" · ")[0]}</strong>{line.split(" · ").slice(1).join(" · ")}</li>
                        ))}
                    </ul>
                </div>
            );
        default: return null;
    }
}

export default function ArticleReader() {
    const { slug } = useParams();
    const article = getArticle(slug);
    if (!article) return <Navigate to="/learn" replace />;

    const insight = VAULT_INSIGHTS.find((i) => i.id === article.insight_id);
    const relatedArticles = (article.related || [])
        .map((s) => getArticle(s))
        .filter(Boolean);

    // Show the VAULT INSIGHT after the second h2, and QuickCheck near the end.
    let h2Seen = 0;
    const insightIndex = article.sections.findIndex((s) => {
        if (s.kind === "h2") h2Seen += 1;
        return h2Seen === 2 && s.kind === "h2";
    });

    return (
        <PublicShell>
            <div className="ar-root" data-testid={`article-reader-${slug}`}>
                <style>{AR_CSS}</style>
                <ReadingProgress />

                <header className="ar-hero" data-testid="article-hero">
                    <div className="ar-hero__bg" style={{ background: TONE_STOP[article.hero_tone] || TONE_STOP.gold }} />
                    <div className="ar-hero__inner">
                        <Link to="/learn" className="ar-back" data-testid="article-back">
                            <ArrowLeft size={13}/> The VAULT Journal
                        </Link>
                        <div className="ar-cat">{article.category.toUpperCase()}</div>
                        <h1 className="ar-title" data-testid="article-title">{article.title}</h1>
                        <p className="ar-deck" data-testid="article-deck">{article.deck}</p>
                        <div className="ar-meta">
                            <Clock size={12}/> {article.reading_time}
                        </div>
                    </div>
                </header>

                <article className="ar-body">
                    {article.sections.map((s, i) => (
                        <div key={i}>
                            <RenderSection s={s} i={i} />
                            {i === insightIndex && insight && (
                                <VaultInsight insight={insight} variant="editorial" />
                            )}
                        </div>
                    ))}

                    {article.quick_check && (
                        <QuickCheck
                            question={article.quick_check.question}
                            options={article.quick_check.options}
                        />
                    )}

                    {/* Progressive CTA finale */}
                    <section className="ar-finale" data-testid="article-finale">
                        <div className="ar-finale__eyebrow">PUT THE IDEA INTO PRACTICE</div>
                        <h3 className="ar-finale__title">Ready to organise your financial life?</h3>
                        <p className="ar-finale__sub">
                            Start with the parts that matter — at your own pace. No CVV. No PIN. No OTP.
                        </p>
                        <div className="ar-finale__row">
                            <Link to="/signup" className="ar-btn ar-btn--gold" data-testid="article-cta-signup">
                                Create Your VAULT <ArrowRight size={14}/>
                            </Link>
                            <Link to="/demo" className="ar-btn ar-btn--ghost" data-testid="article-cta-demo">
                                Explore Demo
                            </Link>
                        </div>
                    </section>

                    {relatedArticles.length > 0 && (
                        <section className="ar-related" data-testid="article-related">
                            <div className="ar-related__eyebrow">RELATED READING</div>
                            <div className="ar-related__grid">
                                {relatedArticles.map((r) => (
                                    <Link
                                        key={r.slug}
                                        to={`/learn/articles/${r.slug}`}
                                        className="ar-related__card"
                                        data-testid={`related-${r.slug}`}
                                    >
                                        <div className="ar-related__cat">{r.category.toUpperCase()}</div>
                                        <div className="ar-related__title">{r.title}</div>
                                        <div className="ar-related__time">
                                            <Clock size={11}/> {r.reading_time}
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        </section>
                    )}
                </article>
            </div>
        </PublicShell>
    );
}

const AR_CSS = `
.ar-root { background: #FBF7EE; color: #0F1E3A; min-height: 100vh; }
.ar-progress { position: fixed; top: 0; left: 0; height: 3px; background: linear-gradient(90deg, #C6A86B, #E6D5A8); z-index: 60; transition: width 120ms ease-out; }
.ar-hero { position: relative; padding: 88px 28px 64px; overflow: hidden; }
.ar-hero__bg { position: absolute; inset: 0; opacity: 0.16; }
.ar-hero__inner { position: relative; max-width: 780px; margin: 0 auto; }
.ar-back { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; color: #57534E; text-decoration: none; margin-bottom: 24px; }
.ar-back:hover { color: #0F1E3A; }
.ar-cat { font-size: 11px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #C6A86B; margin-bottom: 14px; }
.ar-title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: clamp(34px, 5.4vw, 60px); font-weight: 500; letter-spacing: -0.025em; line-height: 1.05; color: #0F1E3A; margin: 0 0 22px; }
.ar-deck { font-size: 19px; line-height: 1.5; color: #44403C; margin: 0 0 22px; }
.ar-meta { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; color: #78716C; letter-spacing: 0.02em; }
.ar-body { max-width: 720px; margin: 0 auto; padding: 40px 28px 96px; }
.ar-lede { font-size: 20px; line-height: 1.55; color: #292524; margin: 0 0 22px; letter-spacing: -0.005em; }
.ar-para { font-size: 17px; line-height: 1.7; color: #292524; margin: 0 0 22px; }
.ar-h2 { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 30px; font-weight: 500; letter-spacing: -0.012em; color: #0F1E3A; margin: 40px 0 18px; }
.ar-pull { margin: 28px -14px; padding: 14px 24px; border-left: 3px solid #C6A86B; font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 26px; font-weight: 500; letter-spacing: -0.015em; line-height: 1.35; color: #0F1E3A; }
.ar-checklist { margin: 20px 0 28px; padding: 20px 24px; background: #FFFFFF; border: 1px solid rgba(15,30,58,0.08); border-radius: 16px; }
.ar-checklist ul { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: 10px; }
.ar-checklist li { font-size: 15px; line-height: 1.55; color: #292524; padding-left: 20px; position: relative; }
.ar-checklist li::before { content: ""; position: absolute; left: 0; top: 9px; width: 8px; height: 8px; border-radius: 999px; background: #C6A86B; }
.ar-checklist li strong { color: #0F1E3A; font-weight: 600; margin-right: 4px; }
.ar-finale { margin: 56px 0 0; padding: 40px 32px; background: linear-gradient(140deg, #0F1E3A 0%, #0A1424 100%); color: #F1F5F9; border-radius: 24px; text-align: center; }
.ar-finale__eyebrow { font-size: 10.5px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #E6D5A8; margin-bottom: 8px; }
.ar-finale__title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 32px; font-weight: 500; letter-spacing: -0.02em; color: #F1F5F9; margin: 0 0 10px; }
.ar-finale__sub { font-size: 15px; line-height: 1.55; color: rgba(241,245,249,0.78); max-width: 480px; margin: 0 auto 20px; }
.ar-finale__row { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; }
.ar-btn { display: inline-flex; align-items: center; gap: 8px; padding: 12px 22px; border-radius: 999px; font-size: 13.5px; font-weight: 600; text-decoration: none; transition: transform 180ms cubic-bezier(0.16,1,0.3,1); }
.ar-btn--gold { background: linear-gradient(180deg,#E6D5A8,#C6A86B); color: #0F1E3A; box-shadow: 0 12px 26px -8px rgba(198,168,107,0.55); }
.ar-btn--gold:hover { transform: translateY(-1px); }
.ar-btn--ghost { background: transparent; color: #F1F5F9; border: 1px solid rgba(241,245,249,0.24); }
.ar-btn--ghost:hover { background: rgba(241,245,249,0.06); }
.ar-related { margin-top: 48px; }
.ar-related__eyebrow { font-size: 10.5px; letter-spacing: 0.24em; text-transform: uppercase; font-weight: 700; color: #78716C; margin-bottom: 14px; }
.ar-related__grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; }
@media (max-width: 720px) { .ar-related__grid { grid-template-columns: 1fr; } }
.ar-related__card { padding: 18px 20px; background: #FFFFFF; border: 1px solid rgba(15,30,58,0.08); border-radius: 16px; text-decoration: none; transition: transform 180ms cubic-bezier(0.16,1,0.3,1), border-color 180ms cubic-bezier(0.16,1,0.3,1), box-shadow 180ms cubic-bezier(0.16,1,0.3,1); }
.ar-related__card:hover { transform: translateY(-2px); border-color: rgba(198,168,107,0.32); box-shadow: 0 16px 32px -18px rgba(15,30,58,0.2); }
.ar-related__cat { font-size: 9.5px; letter-spacing: 0.24em; font-weight: 700; color: #C6A86B; margin-bottom: 6px; }
.ar-related__title { font-family: "Cormorant Garamond", ui-serif, Georgia, serif; font-size: 17px; font-weight: 500; color: #0F1E3A; line-height: 1.22; margin-bottom: 10px; }
.ar-related__time { display: inline-flex; align-items: center; gap: 5px; font-size: 11.5px; color: #78716C; }
@media (prefers-reduced-motion: reduce) { .ar-btn, .ar-related__card, .ar-progress { transition: none; } }
`;
